/*
 * A greeting for the switch: 16-bit PCM mono 8 kHz WAV. Recorded (mic,
 * MediaRecorder) or chosen (any file the browser decodes), then trimmed
 * (silence at both ends, 150 ms kept), normalized (peak -3 dBFS, at most
 * +24 dB) and resampled, like the native core (mt-vox vmstore.c).
 */
export const GREETING_RATE = 8000;
export const MAX_SECONDS = 120;

/** [start, end) of the speech in @s (20 ms frames above -45 dBFS RMS), with 150 ms of air; null if silent. */
export function speechBounds(s: Float32Array, rate: number): [number, number] | null {
  const frame = Math.round(rate / 50);
  const thresh = 0.0056;
  let first = -1;
  let last = -1;
  for (let i = 0; i + frame <= s.length; i += frame) {
    let sum = 0;
    for (let k = 0; k < frame; k++) sum += s[i + k] * s[i + k];
    if (Math.sqrt(sum / frame) > thresh) {
      if (first < 0) first = i;
      last = i + frame;
    }
  }
  if (first < 0) return null;
  const air = Math.round(rate * 0.15);
  return [Math.max(0, first - air), Math.min(s.length, last + air)];
}

/** Peak to -3 dBFS, the gain capped at +24 dB. */
export function normalize(s: Float32Array): Float32Array {
  let peak = 0;
  for (const v of s) peak = Math.max(peak, Math.abs(v));
  const gain = peak > 0 ? Math.min(0.708 / peak, 15.85) : 1;
  return s.map((v) => v * gain);
}

/** 16-bit PCM mono WAV. */
export function encodeWav(s: Float32Array, rate: number): ArrayBuffer {
  const buf = new ArrayBuffer(44 + s.length * 2);
  const v = new DataView(buf);
  const str = (o: number, t: string) => [...t].forEach((ch, i) => v.setUint8(o + i, ch.charCodeAt(0)));
  str(0, "RIFF");
  v.setUint32(4, 36 + s.length * 2, true);
  str(8, "WAVE");
  str(12, "fmt ");
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, rate, true);
  v.setUint32(28, rate * 2, true);
  v.setUint16(32, 2, true);
  v.setUint16(34, 16, true);
  str(36, "data");
  v.setUint32(40, s.length * 2, true);
  for (let i = 0; i < s.length; i++) {
    const x = Math.max(-1, Math.min(1, s[i]));
    v.setInt16(44 + i * 2, x < 0 ? x * 32768 : x * 32767, true);
  }
  return buf;
}

export class GreetingError extends Error {
  constructor(readonly code: "silence" | "too_long" | "unsupported") {
    super(code);
  }
}

/** Any audio the browser decodes -> the switch's WAV and its length. */
export async function toGreeting(data: ArrayBuffer): Promise<{ wav: ArrayBuffer; ms: number }> {
  const ctx = new AudioContext();
  let audio: AudioBuffer;
  try {
    audio = await ctx.decodeAudioData(data);
  } catch {
    throw new GreetingError("unsupported");
  } finally {
    void ctx.close();
  }
  // mono
  const mono = new Float32Array(audio.length);
  for (let c = 0; c < audio.numberOfChannels; c++) {
    const ch = audio.getChannelData(c);
    for (let i = 0; i < ch.length; i++) mono[i] += ch[i] / audio.numberOfChannels;
  }
  const b = speechBounds(mono, audio.sampleRate);
  if (!b) throw new GreetingError("silence");
  const part = mono.subarray(b[0], b[1]);
  if (part.length / audio.sampleRate > MAX_SECONDS) throw new GreetingError("too_long");

  // resample (the offline context low-passes as it converts)
  const n = Math.ceil((part.length * GREETING_RATE) / audio.sampleRate);
  const off = new OfflineAudioContext(1, n, GREETING_RATE);
  const src = off.createBuffer(1, part.length, audio.sampleRate);
  src.copyToChannel(part, 0);
  const node = off.createBufferSource();
  node.buffer = src;
  node.connect(off.destination);
  node.start();
  const out = normalize((await off.startRendering()).getChannelData(0));
  return { wav: encodeWav(out, GREETING_RATE), ms: Math.round((out.length * 1000) / GREETING_RATE) };
}

/** Records the microphone until stop(); then the raw recording (MediaRecorder's format). */
export class MicRecorder {
  private rec?: MediaRecorder;
  private chunks: Blob[] = [];
  private stream?: MediaStream;
  private analyser?: AnalyserNode;
  private ctx?: AudioContext;

  async start(deviceId?: string): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: deviceId ? { deviceId: { exact: deviceId } } : true,
    });
    this.ctx = new AudioContext();
    this.analyser = this.ctx.createAnalyser();
    this.ctx.createMediaStreamSource(this.stream).connect(this.analyser);
    this.chunks = [];
    this.rec = new MediaRecorder(this.stream);
    this.rec.ondataavailable = (e) => this.chunks.push(e.data);
    this.rec.start(250);
  }

  /** 0..1 peak of the last moment */
  level(): number {
    if (!this.analyser) return 0;
    const a = new Float32Array(this.analyser.fftSize);
    this.analyser.getFloatTimeDomainData(a);
    let p = 0;
    for (const v of a) p = Math.max(p, Math.abs(v));
    return p;
  }

  stop(): Promise<ArrayBuffer> {
    return new Promise((resolve) => {
      if (!this.rec) return resolve(new ArrayBuffer(0));
      this.rec.onstop = async () => {
        this.stream?.getTracks().forEach((t) => t.stop());
        void this.ctx?.close();
        resolve(await new Blob(this.chunks).arrayBuffer());
      };
      this.rec.stop();
    });
  }
}
